import React, { useEffect, useMemo, useState } from 'react';
import { getCategoriesForPosition, getCategoryScore, getOverallScore } from '../data/criteria';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ProgressChart from '../components/ProgressChart';
import RadarChart from '../components/RadarChart';
import PageHeader from '../components/PageHeader';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { PlayersAPI, EvaluationsAPI } from '../api';

export default function ProgressPage() {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [evals, setEvals] = useState([]);

  useEffect(() => {
    PlayersAPI.list().then(list => {
      setPlayers(list);
      if (list.length) setPlayerId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!playerId) return;
    EvaluationsAPI.list(playerId).then(list =>
      setEvals(list.sort((a, b) => new Date(a.date) - new Date(b.date)))
    );
  }, [playerId]);

  const player = players.find(p => p.id === playerId);
  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);

  const series = categories.map(c => ({
    id: c.id, name: c.name,
    data: evals.map(e => ({ date: e.date, val: parseFloat(getCategoryScore(e.ratings, c.id, categories)) }))
  }));

  const latest = evals[evals.length - 1];
  const first = evals[0];

  const catData = latest ? categories.map(c => ({
    label: c.name, short: c.short || c.name.split(' ')[0], value: parseFloat(getCategoryScore(latest.ratings, c.id, categories))
  })) : [];

  const deltas = categories.map(c => {
    const start = first ? parseFloat(getCategoryScore(first.ratings, c.id, categories)) : 0;
    const end = latest ? parseFloat(getCategoryScore(latest.ratings, c.id, categories)) : 0;
    return { id: c.id, name: c.name, start, end, delta: +(end - start).toFixed(1) };
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader title="Progress Tracking" subtitle="See how each player is trending category by category." />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-slate-500">Player</Label>
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                {players.map(p => <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  #{p.jerseyNumber}  {p.position}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {player && (
            <div className="flex items-center gap-6">
              <img src={player.avatar} alt="" className="h-14 w-14 rounded-full ring-2 ring-slate-200" />
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {player.firstName} {player.lastName}
                  <Badge className={player.position === 'Goalie' ? 'bg-violet-500/10 text-violet-700 border-violet-500/30' : 'bg-sky-500/10 text-sky-600 border-sky-500/30'}>
                    {player.position}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500">{evals.length} evaluation{evals.length !== 1 ? 's' : ''}  {categories.length} categories</div>
              </div>
              {latest && (
                <div className="text-right pl-6 border-l border-slate-200">
                  <div className="font-mono font-bold text-2xl text-sky-600">{getOverallScore(latest.ratings, categories)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Latest Overall</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold mb-4">Category Trends</h3>
          <ProgressChart series={series} />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold mb-4">Latest Snapshot</h3>
          {latest ? <RadarChart data={catData} /> : <div className="text-slate-500 text-sm">No evaluations yet.</div>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-display font-semibold mb-4">Growth by Category  First to Latest</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {deltas.map(d => {
            const positive = d.delta > 0;
            const zero = d.delta === 0;
            return (
              <div key={d.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{d.name}</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-mono font-bold text-lg text-slate-900">{d.end.toFixed(1)}</span>
                  <span className={`text-xs flex items-center gap-0.5 ${positive ? 'text-emerald-400' : zero ? 'text-slate-500' : 'text-rose-600'}`}>
                    {positive ? <TrendingUp className="h-3 w-3" /> : !zero && <TrendingDown className="h-3 w-3" />}
                    {positive ? '+' : ''}{d.delta}
                  </span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">from {d.start.toFixed(1)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
